import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Logo } from "../components/Brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(user.role === "super_admin" ? "/super" : user.role === "processor" ? "/processor" : "/admin");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(50% 50% at 50% 0%, rgba(29,84,196,0.18), transparent 70%)" }} />
      <div className="relative p-6">
        <Link to="/" data-testid="login-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="relative flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size="lg" /></div>
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-xl">
            <h1 className="text-2xl font-bold">Admin sign in</h1>
            <p className="text-sm text-slate-400 mt-1">Company & platform administrators</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300">Email</label>
                <input
                  data-testid="admin-login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Password</label>
                <input
                  data-testid="admin-login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
                  placeholder="••••••••"
                />
              </div>
              <button
                data-testid="admin-login-submit-button"
                disabled={loading}
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
              </button>
            </form>
            <div className="mt-6 text-xs text-slate-500 border-t border-slate-800 pt-4 space-y-1">
              <p className="font-mono">Demo super admin: daporter37@gmail.com / Admin123!</p>
              <p className="font-mono">Demo company admin: manager@acmecoffee.com / Manager123!</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
